import { Bill, BillData, BillStatus, Trip, Apartment, Traveller, ManualBillItem, generateManualItemId } from '@/types';
import * as billRepository from '@/repository/billRepository';
import { getBillDataForTrip } from './billCalculationService';
import { getSingleApartmentBillData } from './optimizedBillService';
import { billEventService } from './billEventService';

export const billService = {
  listenToBills: (
    tripId: string, 
    callback: (bills: Bill[]) => void
  ): (() => void) => {
    return billRepository.listenToBills(tripId, callback);
  },

  listenToBillsByApartment: (
    tripId: string, 
    apartmentId: string, 
    callback: (bills: Bill[]) => void
  ): (() => void) => {
    return billRepository.listenToBillsByApartment(tripId, apartmentId, callback);
  },

  getBill: (
    tripId: string, 
    billId: string
  ): Promise<Bill | null> => {
    return billRepository.getBill(tripId, billId);
  },

  generateBill: async (
    tripId: string,
    apartmentId: string,
    trip: Trip,
    apartments: Apartment[],
    travellers: Traveller[],
    generatedBy: string,
    generatedByName?: string,
    billRecipientId?: string
  ): Promise<string> => {
    // Use optimized version that only calculates for the specific apartment
    const apartmentBillData = getSingleApartmentBillData(apartmentId, trip, apartments, travellers);
    
    if (!apartmentBillData) {
      throw new Error('No bill data found for apartment');
    }

    // Get apartment travelers with their IDs (only filter once)
    const apartmentTravellers = travellers.filter(t => t.apartmentId === apartmentId);
    
    // Determine bill recipient - use provided ID or default to first traveller
    const recipientId = billRecipientId || apartmentTravellers[0]?.id;
    if (!recipientId) {
      throw new Error('No travellers found in apartment');
    }
    
    // Create the initial "Bill Created" event
    const billCreatedEvent = billEventService.createBillCreatedEvent(generatedBy, generatedByName);
    
    // Create bill from current data
    const bill: Omit<Bill, 'id'> = {
      tripId,
      apartmentId,
      apartmentName: apartmentBillData.apartmentName,
      billRecipientId: recipientId,
      travellers: apartmentBillData.travellers.map((traveller, index) => {
        const fullTraveller = apartmentTravellers[index];
        return {
          id: fullTraveller?.id || `temp-${index}`,
          name: traveller.name,
          address: fullTraveller?.address,
          accommodationCost: traveller.accommodationCost,
          skiPassCost: traveller.skiPassCost,
          transportationCost: traveller.transportationCost,
          membershipFee: traveller.membershipFee,
          childDiscount: traveller.childDiscount,
          total: traveller.total,
        };
      }),
      totalAccommodation: apartmentBillData.totalAccommodation,
      totalSkiPass: apartmentBillData.totalSkiPass,
      totalTransportation: apartmentBillData.totalTransportation,
      totalMembership: apartmentBillData.totalMembership,
      totalChildDiscount: apartmentBillData.totalChildDiscount,
      grandTotal: apartmentBillData.grandTotal,
      status: BillStatus.GENERATED,
      isPaid: false,
      paidAmount: 0,
      generatedAt: new Date(),
      generatedBy,
      events: [billCreatedEvent],
      paymentTransactions: [],
      manualItems: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      // Note: sentAt and paidAt are optional and not set for newly generated bills
    };

    return billRepository.addBill(tripId, bill);
  },

  updateBillStatus: async (
    tripId: string, 
    billId: string, 
    status: BillStatus,
    triggeredBy?: string,
    triggeredByName?: string
  ): Promise<void> => {
    // Get the current bill to access its events
    const currentBill = await billRepository.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const updateData: Partial<Pick<Bill, 'status' | 'sentAt' | 'events'>> = { status };
    const newEvents = [...currentBill.events];
    
    if (status === BillStatus.SENT) {
      updateData.sentAt = new Date();
      // Create a "Bill Sent" event
      const billSentEvent = billEventService.createBillSentEvent(
        triggeredBy || currentBill.generatedBy,
        triggeredByName
      );
      newEvents.push(billSentEvent);
      updateData.events = newEvents;
    }
    
    return billRepository.updateBill(tripId, billId, updateData);
  },

  updatePaymentStatus: async (
    tripId: string, 
    billId: string, 
    isPaid: boolean, 
    paidAmount: number = 0,
    triggeredBy?: string,
    triggeredByName?: string,
    paymentMethod?: string,
    reference?: string
  ): Promise<void> => {
    // Get the current bill to access its events and calculate remaining amount
    const currentBill = await billRepository.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const updateData: Partial<Pick<Bill, 'isPaid' | 'paidAmount' | 'paidAt' | 'status' | 'events'>> = {
      isPaid,
      paidAmount,
    };

    const newEvents = [...currentBill.events];
    const remainingAmount = currentBill.grandTotal - paidAmount;

    if (isPaid && paidAmount >= currentBill.grandTotal) {
      updateData.paidAt = new Date();
      updateData.status = BillStatus.FULLY_PAID;
      
      // Create "Fully Paid" event
      const fullyPaidEvent = billEventService.createFullyPaidEvent(
        currentBill.grandTotal,
        triggeredBy,
        triggeredByName,
        paidAmount - currentBill.paidAmount // This payment amount
      );
      newEvents.push(fullyPaidEvent);
    } else if (paidAmount > currentBill.paidAmount) {
      updateData.status = BillStatus.PARTIALLY_PAID;
      
      // Create "Payment Received" event for the additional payment
      const paymentAmount = paidAmount - currentBill.paidAmount;
      const paymentReceivedEvent = billEventService.createPaymentReceivedEvent(
        paymentAmount,
        paidAmount,
        remainingAmount,
        triggeredBy,
        triggeredByName,
        paymentMethod,
        reference
      );
      newEvents.push(paymentReceivedEvent);
    }

    updateData.events = newEvents;
    return billRepository.updateBill(tripId, billId, updateData);
  },

  deleteBill: (
    tripId: string, 
    billId: string
  ): Promise<void> => {
    return billRepository.deleteBill(tripId, billId);
  },

  /**
   * Add a manual item to a bill
   */
  addManualItem: async (
    tripId: string,
    billId: string,
    description: string,
    amount: number,
    createdBy: string,
    createdByName?: string
  ): Promise<string> => {
    const currentBill = await billRepository.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const manualItemId = generateManualItemId();
    const now = new Date();

    const newManualItem: ManualBillItem = {
      id: manualItemId,
      description,
      amount,
      createdBy,
      createdByName,
      createdAt: now,
      updatedAt: now,
    };

    // Calculate new grand total using centralized function
    const billWithNewItem = {
      ...currentBill,
      manualItems: [...currentBill.manualItems, newManualItem],
    };
    const newGrandTotal = billService.calculateGrandTotal(billWithNewItem);

    // Create a "Bill Changed" event
    const billChangedEvent = billEventService.createBillChangedEvent(
      createdBy,
      createdByName,
      ['manualItems', 'grandTotal'],
      {
        previousGrandTotal: currentBill.grandTotal,
        newGrandTotal,
        manualItemAdded: { description, amount },
      }
    );

    const updateData: Partial<Bill> = {
      manualItems: [...currentBill.manualItems, newManualItem],
      grandTotal: newGrandTotal,
      events: [...currentBill.events, billChangedEvent],
    };

    await billRepository.updateBill(tripId, billId, updateData);
    return manualItemId;
  },

  /**
   * Update a manual item on a bill
   */
  updateManualItem: async (
    tripId: string,
    billId: string,
    manualItemId: string,
    description: string,
    amount: number,
    updatedBy: string,
    updatedByName?: string
  ): Promise<void> => {
    const currentBill = await billRepository.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const itemIndex = currentBill.manualItems.findIndex(item => item.id === manualItemId);
    if (itemIndex === -1) {
      throw new Error('Manual item not found');
    }

    const oldItem = currentBill.manualItems[itemIndex];
    const updatedManualItems = [...currentBill.manualItems];
    updatedManualItems[itemIndex] = {
      ...oldItem,
      description,
      amount,
      updatedAt: new Date(),
    };

    // Calculate new grand total using centralized function
    const billWithUpdatedItem = {
      ...currentBill,
      manualItems: updatedManualItems,
    };
    const newGrandTotal = billService.calculateGrandTotal(billWithUpdatedItem);

    // Create a "Bill Changed" event
    const billChangedEvent = billEventService.createBillChangedEvent(
      updatedBy,
      updatedByName,
      ['manualItems', 'grandTotal'],
      {
        previousGrandTotal: currentBill.grandTotal,
        newGrandTotal,
        manualItemUpdated: {
          id: manualItemId,
          oldDescription: oldItem.description,
          newDescription: description,
          oldAmount: oldItem.amount,
          newAmount: amount,
        },
      }
    );

    const updateData: Partial<Bill> = {
      manualItems: updatedManualItems,
      grandTotal: newGrandTotal,
      events: [...currentBill.events, billChangedEvent],
    };

    await billRepository.updateBill(tripId, billId, updateData);
  },

  /**
   * Delete a manual item from a bill
   */
  deleteManualItem: async (
    tripId: string,
    billId: string,
    manualItemId: string,
    deletedBy: string,
    deletedByName?: string
  ): Promise<void> => {
    const currentBill = await billRepository.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const itemToDelete = currentBill.manualItems.find(item => item.id === manualItemId);
    if (!itemToDelete) {
      throw new Error('Manual item not found');
    }

    const updatedManualItems = currentBill.manualItems.filter(item => item.id !== manualItemId);

    // Calculate new grand total using centralized function
    const billWithDeletedItem = {
      ...currentBill,
      manualItems: updatedManualItems,
    };
    const newGrandTotal = billService.calculateGrandTotal(billWithDeletedItem);

    // Create a "Bill Changed" event
    const billChangedEvent = billEventService.createBillChangedEvent(
      deletedBy,
      deletedByName,
      ['manualItems', 'grandTotal'],
      {
        previousGrandTotal: currentBill.grandTotal,
        newGrandTotal,
        manualItemDeleted: {
          description: itemToDelete.description,
          amount: itemToDelete.amount,
        },
      }
    );

    const updateData: Partial<Bill> = {
      manualItems: updatedManualItems,
      grandTotal: newGrandTotal,
      events: [...currentBill.events, billChangedEvent],
    };

    await billRepository.updateBill(tripId, billId, updateData);
  },

  /**
   * Helper to recalculate grand total including manual items
   */
  calculateGrandTotal: (bill: Bill): number => {
    const baseTotalWithoutManual = 
      bill.totalAccommodation +
      bill.totalSkiPass +
      bill.totalTransportation +
      bill.totalMembership -
      bill.totalChildDiscount;
    const manualItemsTotal = bill.manualItems.reduce(
      (sum, item) => sum + item.amount,
      0
    );
    return baseTotalWithoutManual + manualItemsTotal;
  },
};
